import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import MUIRichTextEditor from 'mui-rte'
import { markdownToDraft } from 'markdown-draft-js'
import debounce from 'lodash/debounce'
import { useFormContext } from 'react-hook-form'
import editorStateToMarkdown from './editor-state-to-markdown'
import { useMarkdownRteFlushRegistration } from './markdown-rte-flush-context'

const DEFAULT_DEBOUNCE_MS = 250

const toDraftDefaultValue = (markdown, markdownToDraftOptions) =>
  JSON.stringify(markdownToDraft(markdown || '', markdownToDraftOptions))

/**
 * Uncontrolled mui-rte field that keeps typing local and syncs markdown to
 * react-hook-form on a debounce (plus flush on unmount / before submit).
 */
const DebouncedMarkdownRichTextEditor = ({
  name,
  label,
  controls,
  className,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  markdownToDraftOptions = {},
  draftToMarkdownOptions = {},
}) => {
  const { register, setValue, getValues } = useFormContext()
  const registerFlusher = useMarkdownRteFlushRegistration()
  const editorStateRef = useRef(null)
  const hasMarkedDirtyRef = useRef(false)
  const defaultValueRef = useRef(null)
  const initialMarkdownRef = useRef(getValues(name) ?? '')
  const setValueRef = useRef(setValue)
  const draftToMarkdownOptionsRef = useRef(draftToMarkdownOptions)

  setValueRef.current = setValue
  draftToMarkdownOptionsRef.current = draftToMarkdownOptions

  if (defaultValueRef.current === null) {
    defaultValueRef.current = toDraftDefaultValue(
      initialMarkdownRef.current,
      markdownToDraftOptions,
    )
  }

  const syncToForm = useMemo(
    () => editorState => {
      if (!editorState) return false

      const markdown = editorStateToMarkdown(
        editorState,
        draftToMarkdownOptionsRef.current,
      )
      const changedFromInitial = markdown !== initialMarkdownRef.current

      // Skip only the mount no-op. After a real edit, always write through —
      // including when the user reverts to the original text — so flush/submit
      // do not keep stale markdown.
      if (!changedFromInitial && !hasMarkedDirtyRef.current) {
        return false
      }

      hasMarkedDirtyRef.current = true

      setValueRef.current(name, markdown, {
        shouldDirty: true,
        shouldValidate: false,
      })

      return changedFromInitial
    },
    [name],
  )

  const debouncedSync = useMemo(
    () =>
      debounce(editorState => {
        syncToForm(editorState)
      }, debounceMs),
    [debounceMs, syncToForm],
  )

  const flush = useMemo(
    () => () => {
      debouncedSync.cancel()
      if (editorStateRef.current) {
        syncToForm(editorStateRef.current)
      }
    },
    [debouncedSync, syncToForm],
  )

  useEffect(() => {
    register(name)
    // GraphQL may return null for optional markdown fields; normalize to a string
    // without marking the form dirty.
    if (getValues(name) == null) {
      setValue(name, initialMarkdownRef.current, {
        shouldValidate: false,
        shouldDirty: false,
      })
    }
    // Register once per field name. Re-running when `register` identity changes
    // (after setValue/dirty updates) can reset the value back to defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  useEffect(() => {
    if (!registerFlusher) return undefined
    return registerFlusher(flush)
  }, [flush, registerFlusher])

  useEffect(
    () => () => {
      flush()
    },
    [flush],
  )

  return (
    <MUIRichTextEditor
      className={className}
      label={label}
      controls={controls}
      defaultValue={defaultValueRef.current}
      onChange={editorState => {
        editorStateRef.current = editorState

        // Resolve mount vs first edit immediately so Save can enable without waiting
        // on the debounce window.
        if (!hasMarkedDirtyRef.current) {
          const changedFromInitial = syncToForm(editorState)
          if (!changedFromInitial) return
          debouncedSync.cancel()
          return
        }

        debouncedSync(editorState)
      }}
    />
  )
}

DebouncedMarkdownRichTextEditor.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  controls: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  debounceMs: PropTypes.number,
  markdownToDraftOptions: PropTypes.object,
  draftToMarkdownOptions: PropTypes.object,
}

export default DebouncedMarkdownRichTextEditor
